import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plus, Minus, ShoppingBag, Loader2, Trash2, Pencil, MapPin } from "lucide-react";
import { ItemOptionsDialog } from "./ItemOptionsDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrdersList = ({ groups, onEdit, onCancel }) => (
  <div className="rounded-2xl border border-sand-300 bg-white p-4" data-testid="waiter-orders">
    <p className="mb-2 font-display text-lg tracking-wide text-slate-900">Today's waiter orders</p>
    {groups.length === 0 ? <p className="py-3 text-center text-sm text-slate-400">None yet</p> : (
      <div className="space-y-2">
        {groups.map((g) => {
          const first = g[0];
          const key = first.ticket || first.id;
          const count = g.reduce((s, o) => s + o.items.reduce((a, i) => a + i.qty, 0), 0);
          const total = g.reduce((s, o) => s + o.total, 0);
          const editable = g.every((o) => o.status === "new");
          const stations = g.map((o) => o.station);
          return (
            <div key={key} data-testid={`waiter-order-${key}`} className="rounded-xl border border-sand-200 p-2.5">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-800">{first.spot_label}</span>
                <div className="flex items-center gap-1.5">
                  {stations.includes("kitchen") && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700">Food</span>}
                  {stations.includes("bar") && <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-sea">Drinks</span>}
                </div>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">{count} items · {total.toFixed(2)}€</p>
              {editable && (
                <div className="mt-1.5 flex gap-2">
                  <button data-testid={`waiter-edit-${key}`} onClick={() => onEdit(g)} className="flex items-center gap-1 rounded-lg bg-sand-100 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-sand-200"><Pencil size={12} /> Edit</button>
                  <button data-testid={`waiter-cancel-${key}`} onClick={() => onCancel(g)} className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-destructive hover:bg-destructive/10"><Trash2 size={12} /> Cancel</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </div>
);

export default function WaiterPage() {
  const [lounges, setLounges] = useState([]);
  const [menu, setMenu] = useState([]);
  const [spot, setSpot] = useState("");
  const [cart, setCart] = useState([]);
  const [note, setNote] = useState("");
  const [allergies, setAllergies] = useState("");
  const [dialogCtx, setDialogCtx] = useState(null);
  const [rules, setRules] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);
  const [sending, setSending] = useState(false);
  const [orders, setOrders] = useState([]);
  const today = format(new Date(), "yyyy-MM-dd");

  const loadOrders = () => axios.get(`${API}/orders`, { params: { date: today } })
    .then((r) => setOrders(r.data.filter((o) => o.source === "waiter"))).catch(() => {});

  useEffect(() => {
    axios.get(`${API}/lounges`).then((r) => setLounges(r.data)).catch(() => {});
    axios.get(`${API}/menu`).then((r) => { setMenu(r.data); setActiveGroup(r.data[0]?.group || "More"); setActiveCat(r.data[0]?.category); }).catch(() => {});
    axios.get(`${API}/option-rules`).then((r) => setRules(r.data)).catch(() => {});
    loadOrders();
    const t = setInterval(loadOrders, 15000);
    return () => clearInterval(t);
  }, []);

  const openItem = (it, cat) => setDialogCtx({ item: it, category: cat.category, group: cat.group, station: cat.group === "Food" ? "kitchen" : "bar" });
  const addLine = (line) => setCart((c) => [...c, { ...line, uid: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` }]);
  const changeQty = (uid, d) => setCart((c) => c.flatMap((l) => (l.uid === uid ? (l.qty + d <= 0 ? [] : [{ ...l, qty: l.qty + d }]) : [l])));
  const lines = cart;
  const count = lines.reduce((s, l) => s + l.qty, 0);
  const total = lines.reduce((s, l) => s + l.price * l.qty, 0);
  const spotLabel = useMemo(() => lounges.find((l) => l.id === spot)?.label || spot, [lounges, spot]);
  const orderGroups = useMemo(() => {
    const map = {};
    orders.forEach((o) => { const k = o.ticket || o.id; (map[k] = map[k] || []).push(o); });
    const gTime = (g) => Math.max(...g.map((o) => new Date(o.created_at).getTime()));
    return Object.values(map).sort((a, b) => gTime(b) - gTime(a));
  }, [orders]);

  const send = async () => {
    if (!spot) { toast.error("Pick a spot first"); return; }
    if (!count) return;
    setSending(true);
    try {
      await axios.post(`${API}/orders`, {
        spot_id: spot, spot_label: spotLabel, note, allergies, source: "waiter",
        items: lines.map((l) => ({ name: l.name, price: l.price, qty: l.qty, station: l.station, options: l.options || null, note: l.note || "" })),
      });
      setCart([]); setNote(""); setAllergies("");
      toast.success(`Order sent for ${spotLabel}`);
      loadOrders();
    } catch { toast.error("Could not send order"); } finally { setSending(false); }
  };

  const cancel = async (group) => {
    try { await Promise.all(group.map((o) => axios.delete(`${API}/orders/${o.id}`))); toast.success("Order cancelled"); loadOrders(); }
    catch { toast.error("Could not cancel"); }
  };
  const edit = async (group) => {
    const first = group[0];
    setSpot(first.spot_id);
    setCart(group.flatMap((o) => o.items.map((it, i) => ({ name: it.name, price: it.price, qty: it.qty, station: it.station || o.station || "bar", options: it.options || null, note: it.note || "", uid: `${Date.now()}-${o.id}-${i}` }))));
    setNote(first.note || ""); setAllergies(first.allergies || "");
    try { await Promise.all(group.map((o) => axios.delete(`${API}/orders/${o.id}`))); loadOrders(); } catch (err) { console.error("Failed to reload order into builder:", err); }
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Loaded into the builder — adjust and resend");
  };

  const groups = useMemo(() => {
    const seen = [];
    menu.forEach((m) => { const g = m.group || "More"; if (!seen.includes(g)) seen.push(g); });
    return seen;
  }, [menu]);
  const groupCats = useMemo(() => menu.filter((m) => (m.group || "More") === activeGroup), [menu, activeGroup]);

  return (
    <div className="mx-auto max-w-6xl p-4 pb-40 lg:grid lg:grid-cols-3 lg:gap-5 lg:pb-4">
      <div className="lg:col-span-2">
        <div className="sticky top-14 z-10 -mx-4 mb-3 bg-sand-100/95 px-4 pt-1 pb-2 backdrop-blur">
          <div className="flex items-center gap-2">
            <MapPin size={18} className="shrink-0 text-brand-700" />
            <select data-testid="waiter-spot-select" value={spot} onChange={(e) => setSpot(e.target.value)}
              className="h-12 flex-1 rounded-xl border border-sand-300 bg-white px-3 text-base outline-none focus:border-brand">
              <option value="">Select a spot…</option>
              {lounges.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
          </div>
          <div className="mt-2 flex gap-2 overflow-x-auto pb-1" data-testid="waiter-groups">
            {groups.map((g) => (
              <button key={g} data-testid={`waiter-group-${g}`} onClick={() => { setActiveGroup(g); const first = menu.find((m) => (m.group || "More") === g); setActiveCat(first?.category); }}
                className={`shrink-0 rounded-full px-4 py-2 text-sm font-bold transition-colors ${activeGroup === g ? "bg-brand text-white shadow" : "bg-white text-slate-700 ring-1 ring-sand-300"}`}>{g}</button>
            ))}
          </div>
          {groupCats.length > 1 && (
            <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
              {groupCats.map((c) => (
                <button key={c.category} onClick={() => { setActiveCat(c.category); document.getElementById(`w-cat-${c.category}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${activeCat === c.category ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-sand-300"}`}>{c.category}</button>
              ))}
            </div>
          )}
        </div>

        {menu.length === 0 && <div className="flex justify-center py-16 text-slate-400"><Loader2 className="animate-spin" /></div>}
        {groupCats.map((cat) => (
          <section key={cat.category} id={`w-cat-${cat.category}`} className="scroll-mt-40 pt-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-brand-700">{cat.category}</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {cat.items.map((item) => {
                const inCart = cart.filter((l) => l.name === item.name).reduce((s, l) => s + l.qty, 0);
                return (
                  <div key={item.name} data-testid={`waiter-item-${item.name}`}
                    className={`flex items-center gap-3 rounded-2xl border-2 bg-white p-4 transition-colors ${inCart > 0 ? "border-brand/50" : "border-transparent"}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-base font-semibold leading-snug text-slate-800">{item.name}</p>
                      {item.desc && <p className="mt-0.5 line-clamp-1 text-xs text-slate-400">{item.desc}</p>}
                      <p className="mt-1 text-sm font-bold text-brand-700">{item.price.toFixed(2)}€</p>
                    </div>
                    <button data-testid={`waiter-add-${item.name}`} onClick={() => openItem(item, cat)}
                      className="flex h-11 shrink-0 items-center gap-1.5 rounded-full bg-slate-900 px-5 text-sm font-semibold text-white transition-colors hover:bg-slate-700"><Plus size={16} /> Add{inCart > 0 ? ` · ${inCart}` : ""}</button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        {/* Waiter orders — shown inline on mobile only */}
        <div className="mt-5 lg:hidden">
          <OrdersList groups={orderGroups} onEdit={edit} onCancel={cancel} />
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:col-span-1 lg:block">
        <div className="sticky top-16 space-y-3">
          <div className="rounded-2xl border border-sand-300 bg-white p-4">
            <p className="mb-2 font-display text-lg tracking-wide text-slate-900">Current order</p>
            {count === 0 ? <p className="py-4 text-center text-sm text-slate-400">No items yet</p> : (
              <ul className="space-y-1.5 text-sm">
                {lines.map((l) => (
                  <li key={l.uid} className="flex items-start gap-2">
                    <div className="flex items-center gap-1 rounded-full bg-sand-100 p-0.5">
                      <button data-testid={`waiter-cart-minus-${l.uid}`} onClick={() => changeQty(l.uid, -1)} className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-brand-700"><Minus size={12} /></button>
                      <span className="w-4 text-center text-xs font-bold">{l.qty}</span>
                      <button onClick={() => changeQty(l.uid, 1)} className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-brand-700"><Plus size={12} /></button>
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="font-semibold text-slate-800">{l.name}</span>
                      {l.options?.length ? <span className="text-slate-500"> · {l.options.join(" · ")}</span> : null}
                      {l.note ? <span className="italic text-slate-400"> — {l.note}</span> : null}
                    </div>
                    <span className="shrink-0 text-slate-400">{(l.price * l.qty).toFixed(2)}€</span>
                  </li>
                ))}
              </ul>
            )}
            <input data-testid="waiter-allergies" value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="Any allergies?"
              className="mt-2 h-9 w-full rounded-lg border border-destructive/40 bg-destructive/5 px-2 text-sm outline-none focus:border-destructive" />
            <input data-testid="waiter-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Comments (optional)"
              className="mt-2 mb-2 h-9 w-full rounded-lg border border-sand-300 bg-sand-50 px-2 text-sm outline-none focus:border-brand" />
            <button data-testid="waiter-send" onClick={send} disabled={sending || !count || !spot}
              className="mt-1 flex h-11 w-full items-center justify-center gap-2 rounded-full bg-brand text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50">
              {sending ? <Loader2 className="animate-spin" size={16} /> : <ShoppingBag size={16} />} Send · {total.toFixed(2)}€
            </button>
          </div>
          <OrdersList groups={orderGroups} onEdit={edit} onCancel={cancel} />
        </div>
      </div>

      {/* Mobile sticky send bar */}
      {count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-sand-300 bg-white/95 p-3 backdrop-blur lg:hidden">
          <div className="mx-auto max-w-6xl">
            <input data-testid="waiter-allergies-mobile" value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="Any allergies?"
              className="mb-2 h-11 w-full rounded-lg border border-destructive/40 bg-destructive/5 px-3 text-sm outline-none focus:border-destructive" />
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Comments (optional)…"
              className="mb-2 h-11 w-full rounded-lg border border-sand-300 bg-sand-50 px-3 text-sm outline-none focus:border-brand" />
            <button data-testid="waiter-send-mobile" onClick={send} disabled={sending || !spot}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 text-base font-semibold text-white hover:bg-brand-600 disabled:opacity-50">
              {sending ? <Loader2 className="animate-spin" size={18} /> : <ShoppingBag size={18} />}
              {spot ? `Send · ${count} item${count > 1 ? "s" : ""} · ${total.toFixed(2)}€` : "Select a spot to send"}
            </button>
          </div>
        </div>
      )}
      <ItemOptionsDialog ctx={dialogCtx} rules={rules} onAdd={addLine} onClose={() => setDialogCtx(null)} />
    </div>
  );
}
